import React from 'react';
import {
  Alert, Button, CardSelector, CollapsibleSection, FormField, JalaliDatePicker, Spinner,
} from '@parsina/framework';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import TaskDialog from '../../components/TaskDialog';

const BACKFILL = `
  mutation Backfill($trialEndsAt: String, $dryRun: Boolean) {
    backfillSubscriptions(trialEndsAt: $trialEndsAt, dryRun: $dryRun) {
      requested created skipped failed
      results { clinicId outcome status detail }
    }
  }
`;

/** Tomorrow, as the Gregorian 'YYYY-MM-DD' every kit date component speaks. */
function tomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Provision every clinic that predates the subscription service.
 *
 * THIS IS THE MOST CONSEQUENTIAL BUTTON IN THE PANEL — one press writes a
 * subscription row for every un-provisioned clinic on the platform — and it used
 * to be a bare `ConfirmDialog` with a sentence and a «ثبت اشتراک» button. Three
 * things were wrong with that, and all three are in the API the client was
 * already calling:
 *
 *   1. `dryRun` EXISTED AND WAS NEVER OFFERED. The service will tell you exactly
 *      what it would do. Not asking, before an estate-wide write, is choosing not
 *      to know.
 *   2. `trialEndsAt` EXISTED AND WAS NEVER OFFERED, so "put the estate on ONE
 *      deadline" — a documented capability, and the obvious thing to want at a
 *      go-live — was reachable only by hand-editing a gRPC call.
 *   3. `results[]` AND `failed` WERE COLLAPSED INTO A TOAST. A partial failure
 *      («created 40, failed 3») showed as a success sentence, and which three
 *      failed was information the panel threw away.
 *
 * So: configure, preview, then commit, in one dialog. The commit stays disabled
 * until a preview of THE SAME inputs has come back — deliberate friction, and the
 * cheapest possible insurance against a mistyped shared deadline.
 *
 * The report stays on screen after the write instead of becoming a toast, because
 * a per-clinic outcome list is not something to read in four seconds.
 */
export default function BackfillDialog({ open, onOpenChange, unprovisioned, onDone }) {
  const { t, formatNumber } = useTranslation();

  const [mode, setMode] = React.useState('grandfather');
  const [trialEndsAt, setTrialEndsAt] = React.useState('');
  const [preview, setPreview] = React.useState(null);   // the dry-run report
  const [report, setReport] = React.useState(null);      // the committed report
  const [busy, setBusy] = React.useState(null);          // 'preview' | 'commit'
  const [error, setError] = React.useState(null);

  // Reset on open so a second visit does not inherit the first one's preview —
  // which would let a stale «created: 40» authorise a write on new inputs.
  React.useEffect(() => {
    if (!open) return;
    setMode('grandfather');
    setTrialEndsAt('');
    setPreview(null);
    setReport(null);
    setBusy(null);
    setError(null);
  }, [open]);

  const trialRequired = mode === 'trial';
  const trialError = (() => {
    if (!trialRequired) return null;
    if (!trialEndsAt) return t('validation.required');
    if (trialEndsAt < tomorrowISO()) return t('validation.futureRequired');
    return null;
  })();

  // The signature of what a preview was taken FOR. Any change to the inputs
  // invalidates it, so the commit button cannot ride on a preview of something
  // else.
  const signature = `${mode}|${trialEndsAt}`;
  const previewValid = preview && preview.signature === signature;

  const run = async (dryRun) => {
    setError(null);
    setBusy(dryRun ? 'preview' : 'commit');
    try {
      const d = await gql(BACKFILL, {
        trialEndsAt: trialRequired ? trialEndsAt : null,
        dryRun,
      });
      const result = d.backfillSubscriptions;
      if (dryRun) setPreview({ ...result, signature });
      else setReport(result);
    } catch (e) {
      setError(errorMessage(e, t));
    } finally {
      setBusy(null);
    }
  };

  const failedRows = (report || preview)?.results?.filter((r) => r.outcome === 'FAILED') || [];
  const shown = report || (previewValid ? preview : null);

  return (
    <TaskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('clinics.backfill.title')}
      error={error}
      pending={busy === 'commit'}
      // Once the write has landed there is nothing unsaved left to guard, so the
      // dialog closes freely and the operator is not asked about a form they
      // already submitted.
      dirty={!report && (trialRequired ? !!trialEndsAt : false)}
      submitLabel={report ? t('common.actions.close') : t('clinics.backfill.commit')}
      submitDisabled={!report && (!previewValid || preview.created === 0 || !!trialError)}
      onSubmit={() => {
        if (report) { onOpenChange(false); onDone(); return; }
        run(false);
      }}
      footerNote={!report && !previewValid ? t('clinics.backfill.needPreview') : undefined}
      data-testid="clinics-backfill-confirm"
    >
      <p className="text-sm leading-6 text-muted-foreground">
        {t('clinics.backfill.lead', { count: unprovisioned })}
      </p>

      {!report && (
        <>
          <FormField label={t('clinics.backfill.modeLabel')}>
            <div data-testid="clinics-backfill-mode">
              <CardSelector
                value={mode}
                onChange={(next) => { setMode(next); setPreview(null); }}
                options={[
                  {
                    value: 'grandfather',
                    label: t('clinics.backfill.grandfather'),
                    description: t('clinics.backfill.grandfatherDesc'),
                  },
                  {
                    value: 'trial',
                    label: t('clinics.backfill.trial'),
                    description: t('clinics.backfill.trialDesc'),
                  },
                ]}
              />
            </div>
          </FormField>

          {/* Only the trial mode needs a date, so the field appears with the
              choice rather than sitting there disabled. */}
          {trialRequired && (
            <FormField
              label={t('clinics.backfill.trialEnd')}
              required
              error={trialEndsAt ? trialError : undefined}
            >
              <div data-testid="clinics-backfill-trial-end">
                <JalaliDatePicker value={trialEndsAt} onChange={setTrialEndsAt} />
              </div>
            </FormField>
          )}

          <div>
            <Button
              variant="outline"
              onClick={() => run(true)}
              disabled={!!busy || !!trialError}
              aria-busy={busy === 'preview' || undefined}
              data-testid="clinics-backfill-preview"
            >
              {busy === 'preview' ? <Spinner size={16} thickness={2} color="currentColor" /> : null}
              {t('clinics.backfill.preview')}
            </Button>
          </div>
        </>
      )}

      {shown ? (
        <Alert
          variant={report ? 'success' : 'info'}
          data-testid="clinics-backfill-report"
        >
          {t('clinics.backfill.previewResult', {
            created: shown.created,
            skipped: shown.skipped,
            failed: shown.failed,
          })}
        </Alert>
      ) : null}

      {/* Which ones failed, and why. Collapsed, because the common case is none —
          but present, because a partial failure that nobody can enumerate is a
          partial failure nobody fixes. */}
      {failedRows.length ? (
        <CollapsibleSection
          title={t('clinics.backfill.failedTitle')}
          badge={formatNumber(failedRows.length)}
        >
          <ul className="flex flex-col gap-1.5">
            {failedRows.map((row) => (
              <li key={row.clinicId} className="text-xs">
                <span {...LTR_TEXT} className="font-mono text-muted-foreground">
                  {row.clinicId}
                </span>
                <span className="ms-2" dir="auto">{row.detail}</span>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      ) : null}
    </TaskDialog>
  );
}
